
import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ChevronDown,
  FileText,
  Mail,
  Menu,
  Phone,
  Sparkles,
  X,
} from "lucide-react";
import {
  FaFacebookF,
  FaInstagram,
  FaTwitter,
} from "react-icons/fa";

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuButtonRef = useRef(null);
  const [navbarHeight, setNavbarHeight] = useState(0);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileDropdown, setMobileDropdown] = useState(null);

  const location = useLocation();

  const getActiveTabFromPath = () => {
    const path = location.pathname;

    const routes = {
      "/": "home",
      "/about-us": "about-us",
      "/director-desk": "director-desk",
      "/principal-desk": "principal-desk",
      "/mandatory-disclosure": "mandatory-disclosure",
      "/cbse-details": "cbse-detail",
      "/news": "news",
      "/facilities": "facility",
      "/admission": "admission",
      "/student-tc": "student-tc",
      "/fee-structure": "fee",
      "/gallery": "school-gallery",
      "/video-gallery": "video-gallery",
      "/enquiry": "enquiry",
    };

    return routes[path] || "home";
  };

  const activeTab = getActiveTabFromPath();

  useEffect(() => {
    const updateHeight = () => {
      const nav = document.querySelector("nav");

      if (nav) {
        setNavbarHeight(nav.offsetHeight);
      }
    };

    updateHeight();

    window.addEventListener("resize", updateHeight);

    return () => {
      window.removeEventListener("resize", updateHeight);
    };
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    setOpenDropdown(null);
    setMobileDropdown(null);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMenuOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        closeMenu();
      }
    };

    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isMenuOpen]);

  const toggleMenu = () => {
    setIsMenuOpen((current) => !current);
  };

  const closeMenu = () => {
    if (document.activeElement?.closest?.('[data-mobile-sidebar]')) {
      menuButtonRef.current?.focus();
    }
    setIsMenuOpen(false);
    setOpenDropdown(null);
    setMobileDropdown(null);
  };

  const navLinks = [
    {
      name: "Home",
      path: "/",
      id: "home",
    },
    {
      name: "About Us",
      id: "about",
      hasDropdown: true,
    },
    {
      name: "News",
      path: "/news",
      id: "news",
    },
    {
      name: "Facility",
      path: "/facilities",
      id: "facility",
    },
    {
      name: "Admission",
      id: "admission",
      hasDropdown: true,
    },
    {
      name: "Fee Structure",
      path: "/fee-structure",
      id: "fee",
    },
    {
      name: "Gallery",
      id: "gallery",
      hasDropdown: true,
    },
  ];

  const aboutDropdownItems = [
    {
      name: "About Us",
      path: "/about-us",
      id: "about-us",
    },
    {
      name: "Director Desk",
      path: "/director-desk",
      id: "director-desk",
    },
    {
      name: "Principal Desk",
      path: "/principal-desk",
      id: "principal-desk",
    },
    {
      name: "Mandatory Disclosure",
      path: "/mandatory-disclosure",
      id: "mandatory-disclosure",
    },
    {
      name: "C.B.S.E. Detail",
      path: "/cbse-details",
      id: "cbse-detail",
    },
  ];

  const admissionDropdownItems = [
    {
      name: "Admission",
      path: "/admission",
      id: "admission",
    },
    {
      name: "Student TC",
      path: "/student-tc",
      id: "student-tc",
    },
  ];

  const galleryDropdownItems = [
    {
      name: "School Gallery",
      path: "/gallery",
      id: "school-gallery",
    },
    {
      name: "Video Gallery",
      path: "/video-gallery",
      id: "video-gallery",
    },
  ];

  const getDropdownItems = (id) => {
    if (id === "about") return aboutDropdownItems;
    if (id === "admission") return admissionDropdownItems;
    if (id === "gallery") return galleryDropdownItems;
    return [];
  };

  const isDropdownActive = (id) => {
    if (id === "about") {
      return aboutDropdownItems.some(
        (item) => item.id === activeTab
      );
    }

    if (id === "admission") {
      return admissionDropdownItems.some(
        (item) => item.id === activeTab
      );
    }

    if (id === "gallery") {
      return galleryDropdownItems.some(
        (item) => item.id === activeTab
      );
    }

    return false;
  };

  const socialLinks = [
    {
      name: "Facebook",
      Icon: FaFacebookF,
      desktopColor: "hover:text-blue-200",
      mobileColor: "text-red-700",
    },
    {
      name: "Instagram",
      Icon: FaInstagram,
      desktopColor: "hover:text-pink-200",
      mobileColor: "text-red-700",
    },
    {
      name: "Twitter",
      Icon: FaTwitter,
      desktopColor: "hover:text-blue-200",
      mobileColor: "text-red-700",
    },
  ];

  return (
    <>
      <nav className="fixed left-0 right-0 top-0 z-50 w-full">
        <div className="relative bg-gradient-to-r from-red-800 via-red-700 to-yellow-800 text-white shadow-md">
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0" />

          <div className="relative w-full px-3 py-2 sm:px-4 sm:py-2.5 md:px-6">
            <div className="flex flex-col items-center justify-between gap-2 text-[10px] sm:text-xs lg:flex-row lg:text-sm">
              <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 lg:justify-start lg:gap-x-6">
                <a
                  href="mailto:st.xavierbelavns@gmail.com"
                  className="group flex items-center gap-1.5 transition-all duration-300 hover:scale-105 hover:text-blue-200"
                >
                  <Mail
                    size={12}
                    className="shrink-0 transition-transform group-hover:rotate-12 sm:size-[14px]"
                  />
                  <span className="hidden xs:inline">
                    st.xavierbelavns@gmail.com
                  </span>
                </a>

                <a
                  href="tel:+918601017877"
                  className="group flex items-center gap-1.5 transition-all duration-300 hover:scale-105 hover:text-blue-200"
                >
                  <Phone
                    size={12}
                    className="shrink-0 transition-transform group-hover:rotate-12 sm:size-[14px]"
                  />
                  <span className="whitespace-nowrap">
                    +91 86010 17877
                  </span>
                </a>

                <a
                  href="tel:+919450785369"
                  className="group hidden items-center gap-1.5 transition-all duration-300 hover:scale-105 hover:text-blue-200 sm:flex"
                >
                  <Phone
                    size={12}
                    className="shrink-0 transition-transform group-hover:rotate-12 sm:size-[14px]"
                  />
                  <span className="whitespace-nowrap">
                    +91 94507 85369
                  </span>
                </a>
              </div>

              <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 lg:justify-end lg:gap-x-4">
                <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 whitespace-nowrap font-medium sm:gap-x-3">
                  <span>
                    School Code:{" "}
                    <strong className="font-bold text-yellow-300">
                      71391
                    </strong>
                  </span>

                  <span className="hidden h-4 w-px bg-white/40 sm:block" />

                  <span>
                    Affiliation No:{" "}
                    <strong className="font-bold text-yellow-300">
                      2132568
                    </strong>
                  </span>
                </div>

                <span className="hidden h-5 w-px bg-white/40 sm:block" />

                <div className="flex items-center gap-3 sm:gap-4">
                  {socialLinks.map((social) => {
                    const SocialIcon = social.Icon;

                    return (
                      <span
                        key={social.name}
                        role="img"
                        aria-label={social.name}
                        title={social.name}
                        className={`inline-flex items-center justify-center transition-all duration-300 hover:rotate-12 hover:scale-110 ${social.desktopColor}`}
                      >
                        <SocialIcon
                          size={11}
                          className="sm:size-[14px]"
                        />
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="relative border-b border-blue-100 bg-white shadow-[0_8px_30px_rgb(0,0,0,0.12)]">
          <div className="absolute left-0 right-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-blue-500 to-transparent" />

          <div className="w-full px-3 sm:px-4 md:px-8">
            <div className="flex min-h-[60px] items-center justify-between sm:min-h-[64px] md:min-h-[70px] lg:min-h-[90px]">
              <Link
                to="/"
                className="group relative flex cursor-pointer items-center py-1 transition-all duration-500 hover:scale-105"
              >
                <div className="absolute inset-0 rounded-full bg-blue-400/20 opacity-0 blur-xl transition-opacity duration-500 group-hover:opacity-100" />

                <img
                  src="/logo.png"
                  alt="St. Xavier's School Logo"
                  className="relative z-10 h-14 w-auto object-contain sm:h-16 md:h-18 lg:h-18"
                  onError={(event) => {
                    event.currentTarget.onerror = null;
                    event.currentTarget.src =
                      "https://via.placeholder.com/120x120?text=Logo";
                  }}
                />
              </Link>

              <div className="hidden items-center gap-3 lg:flex xl:gap-6">
                {navLinks.map((link) => {
                  const dropdownItems = getDropdownItems(link.id);

                  const isActive = link.hasDropdown
                    ? isDropdownActive(link.id)
                    : activeTab === link.id;

                  return (
                    <div
                      key={link.id}
                      className="group relative"
                      onMouseEnter={() => {
                        if (link.hasDropdown) {
                          setOpenDropdown(link.id);
                        }
                      }}
                      onMouseLeave={() => {
                        if (link.hasDropdown) {
                          setOpenDropdown(null);
                        }
                      }}
                    >
                      {link.hasDropdown ? (
                        <>
                          <button
                            type="button"
                            onClick={() => {
                              setOpenDropdown((current) =>
                                current === link.id
                                  ? null
                                  : link.id
                              );
                            }}
                            aria-expanded={
                              openDropdown === link.id
                            }
                            aria-haspopup="true"
                            className={`relative flex items-center gap-1 whitespace-nowrap py-6 text-sm font-medium text-gray-700 transition-all duration-300 hover:text-blue-600 xl:text-base ${
                              isActive ? "text-blue-600" : ""
                            }`}
                          >
                            {link.name}

                            <ChevronDown
                              size={14}
                              className={`transition-transform duration-300 ${
                                openDropdown === link.id
                                  ? "rotate-180"
                                  : ""
                              }`}
                            />

                            <span
                              className={`absolute bottom-3 left-1/2 h-0.5 w-0 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 transition-all duration-300 group-hover:left-0 group-hover:w-full ${
                                isActive ? "left-0 w-full" : ""
                              }`}
                            />
                          </button>

                          {openDropdown === link.id && (
                            <div className="animate-fadeInDown absolute left-0 top-full z-50 mt-0 w-64 overflow-hidden rounded-xl border border-gray-100 bg-white shadow-xl">
                              {dropdownItems.map((item) => (
                                <Link
                                  key={item.id}
                                  to={item.path}
                                  onClick={closeMenu}
                                  className={`block w-full px-5 py-3 text-left text-sm text-gray-700 transition-all duration-300 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 hover:text-blue-600 ${
                                    activeTab === item.id
                                      ? "bg-blue-50 text-blue-600"
                                      : ""
                                  }`}
                                >
                                  {item.name}
                                </Link>
                              ))}
                            </div>
                          )}
                        </>
                      ) : (
                        <Link
                          to={link.path}
                          className={`relative block whitespace-nowrap py-6 text-sm font-medium text-gray-700 transition-all duration-300 hover:text-blue-600 xl:text-base ${
                            isActive ? "text-blue-600" : ""
                          }`}
                        >
                          {link.name}

                          <span
                            className={`absolute bottom-3 left-1/2 h-0.5 w-0 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 transition-all duration-300 group-hover:left-0 group-hover:w-full ${
                              isActive ? "left-0 w-full" : ""
                            }`}
                          />
                        </Link>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="hidden items-center gap-2 lg:flex xl:gap-3">
                <Link
                  to="/mandatory-disclosure"
                  className={`flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-semibold shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md xl:px-4 xl:py-2 xl:text-sm ${
                    activeTab === "mandatory-disclosure"
                      ? "border-red-700 bg-red-700 text-white"
                      : "border-red-700 bg-white text-red-700 hover:bg-red-700 hover:text-white"
                  }`}
                >
                  <FileText size={15} />
                  <span>Mandatory Disclosure</span>
                </Link>

                <Link
                  to="/enquiry"
                  className={`group flex items-center gap-2 whitespace-nowrap rounded-full bg-gradient-to-r px-3 py-1.5 text-xs font-medium text-white shadow-md transition-all duration-300 hover:scale-105 hover:shadow-xl xl:px-5 xl:py-2 xl:text-sm ${
                    activeTab === "enquiry"
                      ? "from-orange-500 to-orange-600"
                      : "from-blue-900 to-indigo-900"
                  }`}
                >
                  <Sparkles
                    size={14}
                    className="transition-transform group-hover:rotate-12 xl:size-[16px]"
                  />
                  <span>Enquiry Now</span>
                </Link>
              </div>

              <button
                type="button"
                ref={menuButtonRef}
                onClick={toggleMenu}
                aria-expanded={isMenuOpen}
                aria-label={
                  isMenuOpen ? "Close menu" : "Open menu"
                }
                className="z-50 flex items-center justify-center rounded-xl bg-gray-50 p-2.5 text-gray-700 shadow-sm transition-all duration-300 hover:scale-110 hover:text-blue-600 active:scale-95 lg:hidden"
              >
                {isMenuOpen ? (
                  <X size={22} />
                ) : (
                  <Menu size={22} />
                )}
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div
        style={{ height: `${navbarHeight}px` }}
        className="w-full"
      />

      <button
        type="button"
        className={`fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px] transition-all duration-300 lg:hidden ${
          isMenuOpen
            ? "visible opacity-100"
            : "pointer-events-none invisible opacity-0"
        }`}
        onClick={closeMenu}
        aria-label="Close menu overlay"
        tabIndex={isMenuOpen ? 0 : -1}
      />

      <aside
        data-mobile-sidebar
        inert={!isMenuOpen}
        className={`fixed right-0 top-0 z-50 flex h-full w-[88%] max-w-[370px] flex-col overflow-hidden bg-[#FFFCFA] shadow-2xl transition-transform duration-300 ease-out lg:hidden ${
          isMenuOpen
            ? "translate-x-0"
            : "translate-x-full"
        }`}
      >
        <div className="relative shrink-0 overflow-hidden bg-gradient-to-r from-[#681515] via-red-800 to-[#a42626] px-5 pb-5 pt-4 text-white">
          <div className="pointer-events-none absolute -right-10 -top-10 h-36 w-36 rounded-full border-[22px] border-white/5" />

          <div className="relative flex items-center justify-between gap-4">
            <Link
              to="/"
              onClick={closeMenu}
              tabIndex={isMenuOpen ? 0 : -1}
              className="flex items-center"
            >
              <img
                src="/logo.png"
                alt="St. Xavier's School Logo"
                className="h-[85px] w-auto max-w-[150px] object-contain"
              />
            </Link>

            <button
              type="button"
              onClick={closeMenu}
              tabIndex={isMenuOpen ? 0 : -1}
              aria-label="Close mobile navigation"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/20 bg-white/10 text-white transition hover:bg-white/20"
            >
              <X size={22} />
            </button>
          </div>

          <div className="relative mt-4 flex flex-wrap gap-x-4 gap-y-1.5 border-t border-white/20 pt-3 text-[12px] text-white/85">
            <span>
              School Code:{" "}
              <span className="font-medium text-yellow-300">
                71391
              </span>
            </span>

            <span>
              Affiliation No:{" "}
              <span className="font-medium text-yellow-300">
                2132568
              </span>
            </span>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-5">
          <div className="mb-4 flex items-center justify-between px-2">
            <h2 className="text-[12px] font-medium uppercase tracking-[0.14em] text-red-800">
              Explore Website
            </h2>

            <span className="h-[2px] w-12 rounded-full bg-amber-400" />
          </div>

          <div className="space-y-1.5">
            {navLinks.map((link) => {
              const dropdownItems = getDropdownItems(link.id);

              const isActive = link.hasDropdown
                ? isDropdownActive(link.id)
                : activeTab === link.id;

              const expanded = mobileDropdown === link.id;

              return (
                <div key={link.id}>
                  {link.hasDropdown ? (
                    <>
                      <button
                        type="button"
                        disabled={!isMenuOpen}
                        onClick={() => {
                          setMobileDropdown((current) =>
                            current === link.id
                              ? null
                              : link.id
                          );
                        }}
                        aria-expanded={expanded}
                        className={`flex w-full items-center justify-between rounded-xl px-4 py-3.5 text-left text-[15px] font-medium transition-all duration-200 ${
                          isActive || expanded
                            ? "border border-red-100 bg-red-50 text-red-800"
                            : "border border-transparent text-slate-700 hover:bg-red-50/60 hover:text-red-800"
                        }`}
                      >
                        <span>{link.name}</span>

                        <span
                          className={`flex h-7 w-7 items-center justify-center rounded-lg transition-colors ${
                            expanded
                              ? "bg-red-800 text-white"
                              : "bg-slate-100 text-slate-500"
                          }`}
                        >
                          <ChevronDown
                            size={16}
                            className={`transition-transform duration-300 ${
                              expanded ? "rotate-180" : ""
                            }`}
                          />
                        </span>
                      </button>

                      <div
                        className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${
                          expanded
                            ? "grid-rows-[1fr]"
                            : "grid-rows-[0fr]"
                        }`}
                      >
                        <div className="overflow-hidden">
                          <div className="mb-2 ml-5 mt-2 space-y-1 border-l-2 border-red-100 pl-3">
                            {dropdownItems.map((item) => {
                              const childActive =
                                activeTab === item.id;

                              return (
                                <Link
                                  key={item.id}
                                  to={item.path}
                                  onClick={closeMenu}
                                  tabIndex={
                                    isMenuOpen && expanded
                                      ? 0
                                      : -1
                                  }
                                  className={`flex items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-[13px] transition-colors ${
                                    childActive
                                      ? "bg-red-50 font-medium text-red-800"
                                      : "text-slate-600 hover:bg-red-50 hover:text-red-800"
                                  }`}
                                >
                                  <span>{item.name}</span>

                                  <ChevronDown
                                    size={13}
                                    className="-rotate-90 text-amber-600"
                                  />
                                </Link>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <Link
                      to={link.path}
                      onClick={closeMenu}
                      tabIndex={isMenuOpen ? 0 : -1}
                      className={`flex w-full items-center justify-between rounded-xl border px-4 py-3.5 text-[15px] font-medium transition-colors ${
                        isActive
                          ? "border-red-100 bg-red-50 text-red-800"
                          : "border-transparent text-slate-700 hover:bg-red-50/60 hover:text-red-800"
                      }`}
                    >
                      <span>{link.name}</span>

                      {isActive && (
                        <span className="h-2 w-2 rounded-full bg-amber-500" />
                      )}
                    </Link>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-6 space-y-3 border-t border-red-100 pt-5">
            <Link
              to="/mandatory-disclosure"
              onClick={closeMenu}
              tabIndex={isMenuOpen ? 0 : -1}
              className={`flex w-full items-center justify-center gap-2 rounded-xl border px-4 py-3.5 text-[14px] font-medium transition-colors ${
                activeTab === "mandatory-disclosure"
                  ? "border-red-800 bg-red-800 text-white"
                  : "border-red-200 bg-white text-red-800 hover:bg-red-50"
              }`}
            >
              <FileText size={18} />
              Mandatory Disclosure
            </Link>

            <Link
              to="/enquiry"
              onClick={closeMenu}
              tabIndex={isMenuOpen ? 0 : -1}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-red-900 to-red-700 px-4 py-3.5 text-[14px] font-medium text-white shadow-sm transition hover:from-red-800 hover:to-red-600"
            >
              <Phone size={17} />
              Enquiry Now
            </Link>
          </div>

          <div className="mt-6 border-t border-red-100 pt-5">
            <h3 className="mb-4 px-2 text-[13px] font-medium uppercase tracking-wide text-red-800">
              Contact Us
            </h3>

            <div className="space-y-3">
              <a
                href="tel:+918601017877"
                tabIndex={isMenuOpen ? 0 : -1}
                className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-[13px] text-slate-700 transition-colors hover:bg-red-50 hover:text-red-800"
              >
                <Phone size={17} className="shrink-0 text-red-700" />
                +91 86010 17877
              </a>

              <a
                href="tel:+919450785369"
                tabIndex={isMenuOpen ? 0 : -1}
                className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-[13px] text-slate-700 transition-colors hover:bg-red-50 hover:text-red-800"
              >
                <Phone size={17} className="shrink-0 text-red-700" />
                +91 94507 85369
              </a>

              <a
                href="mailto:st.xavierbelavns@gmail.com"
                tabIndex={isMenuOpen ? 0 : -1}
                className="flex items-start gap-3 rounded-xl px-3 py-2.5 text-[13px] text-slate-700 transition-colors hover:bg-red-50 hover:text-red-800"
              >
                <Mail
                  size={17}
                  className="mt-0.5 shrink-0 text-red-700"
                />
                <span className="break-all">
                  st.xavierbelavns@gmail.com
                </span>
              </a>
            </div>

            <div className="mt-5 flex items-center gap-5 border-t border-slate-100 px-3 pt-4">
              {socialLinks.map((social) => {
                const SocialIcon = social.Icon;

                return (
                  <span
                    key={social.name}
                    role="img"
                    aria-label={social.name}
                    title={social.name}
                    className={social.mobileColor}
                  >
                    <SocialIcon size={18} />
                  </span>
                );
              })}
            </div>
          </div>
        </div>
      </aside>

      <style>{`
        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fadeInDown {
          animation: fadeInDown 0.3s ease-out;
        }

        @media (min-width: 480px) {
          .xs\\:inline {
            display: inline;
          }
        }

        @media (max-width: 479px) {
          .xs\\:inline {
            display: none;
          }
        }
      `}</style>
    </>
  );
};

export default Navbar;


